import React, { FC, useState } from "react"
import { KSpan, KTitleSpan } from "kahuna-base-react-components"
import { AdvanceDealAdvance, AdvanceDealDetails } from "../../../../../types"
import Loading from "../../../../ui/Loading"
import { STATUS_STYLE } from "./dealStatus"
import AdvanceRecoupmentTimeline from "./AdvanceRecoupmentTimeline"

const GREEN = "#22c55e"
const RED = "#ef4444"
const TEXT_MUTED = "#6b7280"
const TEXT_PRIMARY = "#111"
// Fully company-recouped rows: a restrained pastel green, never the saturated chart green.
const RECOUPED_ROW_BG = "#ecf7ea"

const usd0 = (value: number | null | undefined) =>
  value === null || value === undefined || isNaN(value) ? "N/A" : `$${Math.round(value).toLocaleString("en-US")}`

// Backend months are "YYYYMM".
const monthYear = (key: string | null | undefined) => {
  if (!key) return "N/A"
  const month = parseInt(key.slice(4, 6), 10)
  if (key.length < 6 || isNaN(month)) return "N/A"
  return new Date(parseInt(key.slice(0, 4), 10), month - 1, 1).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric"
  })
}

const monthsOrNA = (value: number | null | undefined) =>
  value === null || value === undefined || isNaN(value) ? "N/A" : `${Math.round(value)} mo`

const thStyle = (numeric: boolean): React.CSSProperties => ({
  textAlign: numeric ? "right" : "left",
  padding: "8px 12px",
  fontSize: 12,
  fontWeight: 500,
  color: TEXT_MUTED,
  borderBottom: "1px solid #e5e7eb",
  whiteSpace: "nowrap"
})

const tdStyle = (numeric: boolean): React.CSSProperties => ({
  textAlign: numeric ? "right" : "left",
  padding: "10px 12px",
  fontSize: 13,
  color: TEXT_PRIMARY,
  borderBottom: "1px solid #f1f5f9",
  verticalAlign: "top",
  whiteSpace: "nowrap"
})

const Muted: FC<{ text: string }> = ({ text }) => <KSpan text={text} fontSize={13} color={TEXT_MUTED} />

// Primary value with a smaller muted line under it.
const Stack: FC<{ primary: string; secondary?: string }> = ({ primary, secondary }) => (
  <div className="flex flex-col gap-0.5">
    <KSpan text={primary} fontSize={13} color={TEXT_PRIMARY} />
    {secondary ? <KSpan text={secondary} fontSize={11.5} color={TEXT_MUTED} /> : null}
  </div>
)

const Delta: FC<{ value: number | null | undefined }> = ({ value }) => {
  if (value === null || value === undefined || isNaN(value)) return <Muted text="N/A" />
  const rounded = Math.round(value)
  return (
    <KSpan
      text={`${rounded > 0 ? "+" : ""}${rounded} mo`}
      fontSize={13}
      fontWeight={500}
      color={rounded > 0 ? GREEN : rounded < 0 ? RED : TEXT_MUTED}
    />
  )
}

const StatusPill: FC<{ status: string }> = ({ status }) => {
  const style = STATUS_STYLE[status] || { label: status, bg: "#f1f5f9", color: TEXT_MUTED }
  return (
    <span
      style={{
        display: "inline-flex",
        backgroundColor: style.bg,
        padding: "3px 12px",
        borderRadius: 999,
        whiteSpace: "nowrap"
      }}
    >
      <KSpan text={style.label} fontSize={12} fontWeight={600} color={style.color} lineHeight="16px" />
    </span>
  )
}

// What the earlier advances were doing: still blocking this one, or already cleared.
const previousAdvanceCell = (advance: AdvanceDealAdvance) => {
  if (advance.advanceNumber <= 1) return <Muted text="—" />
  if (advance.waitsForAdvanceId === null || advance.waitsForAdvanceId === undefined) {
    return <Stack primary="Recouped" />
  }
  return (
    <Stack
      primary="In progress"
      secondary={advance.waitingMonths === null ? "No projected recoup" : `${advance.waitingMonths} mo remaining`}
    />
  )
}

type ScheduleView = "table" | "timeline"

// Segmented switch: the active option is a raised white chip on a grey track.
const ViewToggle: FC<{ view: ScheduleView; onChange: (view: ScheduleView) => void }> = ({ view, onChange }) => (
  <div className="inline-flex rounded-full p-0.5" style={{ background: "#f1f5f9" }} role="tablist">
    {(["table", "timeline"] as ScheduleView[]).map((option) => {
      const active = option === view
      return (
        <button
          key={option}
          type="button"
          role="tab"
          aria-selected={active}
          onClick={() => onChange(option)}
          className="rounded-full px-3 py-1 inline-flex items-center justify-center transition-colors"
          style={{
            background: active ? "#fff" : "transparent",
            boxShadow: active ? "0 1px 2px rgba(0,0,0,0.12)" : "none"
          }}
        >
          {/* Plain span with a fixed line height, like the overview's month badge, so the text
              sits dead centre in the chip. */}
          <span
            style={{
              fontSize: 12,
              lineHeight: "18px",
              fontWeight: active ? 600 : 500,
              color: active ? TEXT_PRIMARY : TEXT_MUTED
            }}
          >
            {option === "table" ? "Table" : "Timeline"}
          </span>
        </button>
      )
    })}
  </div>
)

type AdvanceRecoupmentScheduleProps = {
  details: AdvanceDealDetails | null
  loading: boolean
  failed: boolean
}

const AdvanceRecoupmentSchedule: FC<AdvanceRecoupmentScheduleProps> = ({ details, loading, failed }) => {
  const [view, setView] = useState<ScheduleView>("table")

  const advances = details?.advances ?? []
  // Column totals over the listed advances. Months are only summed when every advance has one,
  // so a deal with an unprojected advance reads N/A rather than a misleadingly low total.
  const sum = (values: number[]) => values.reduce((total, value) => total + value, 0)
  const projectedMonths = advances.map((a) => (a.isActual ? a.actualRecoupMonths : a.projectedRecoupMonths))
  const totals = {
    amount: sum(advances.map((a) => a.amount)),
    outstanding: sum(advances.map((a) => a.companyOutstanding)),
    targetMonths: sum(advances.map((a) => a.targetMonths)),
    projectedMonths: projectedMonths.every((m) => m !== null && m !== undefined)
      ? sum(projectedMonths as number[])
      : null
  }
  const totalDelta = totals.projectedMonths === null ? null : totals.targetMonths - totals.projectedMonths
  // Same thresholds as the rest of the page: more than 2 months ahead / behind leaves On Track.
  const totalStatus =
    totalDelta === null ? null : totalDelta > 2 ? "ahead" : totalDelta < -2 ? "timing_review" : "on_track"

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex flex-col gap-4 min-w-0">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <KTitleSpan text="Advance Recoupment Schedule" fontSize={20} fontWeight={700} lineHeight="28px" />
        <ViewToggle view={view} onChange={setView} />
      </div>
      {loading ? (
        <div className="flex justify-center items-center min-h-[120px]">
          <Loading />
        </div>
      ) : failed || !details ? (
        <KSpan text="The advance schedule could not be loaded." fontSize={13} color={TEXT_MUTED} />
      ) : view === "timeline" ? (
        <AdvanceRecoupmentTimeline advances={details.advances} />
      ) : (
        <div className="rounded-xl border border-gray-200" style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1280 }}>
            <thead>
              <tr>
                <th style={thStyle(false)}>Advance</th>
                <th style={thStyle(false)}>Deployment</th>
                <th style={thStyle(true)}>Amount</th>
                <th style={thStyle(true)}>Outstanding</th>
                <th style={thStyle(false)}>Previous Advance at Deployment</th>
                <th style={thStyle(false)}>Recoup Start</th>
                <th style={thStyle(false)}>Target Company Recoup</th>
                <th style={thStyle(false)}>Projection / Actual</th>
                <th style={thStyle(true)}>Delta</th>
                <th style={thStyle(false)}>Status</th>
              </tr>
            </thead>
            <tbody>
              {details.advances.map((advance) => {
                const rowStyle = advance.companyRecouped ? { backgroundColor: RECOUPED_ROW_BG } : undefined
                const months = advance.isActual ? advance.actualRecoupMonths : advance.projectedRecoupMonths
                const cell = (numeric: boolean): React.CSSProperties => ({ ...tdStyle(numeric), ...rowStyle })
                return (
                  <tr key={advance.advanceId}>
                    <td style={cell(false)}>
                      <KSpan text={`#${advance.advanceNumber}`} fontSize={13} fontWeight={700} color={TEXT_PRIMARY} />
                    </td>
                    <td style={cell(false)}>{monthYear(advance.deploymentMonth)}</td>
                    <td style={cell(true)}>{usd0(advance.amount)}</td>
                    <td style={cell(true)}>{usd0(advance.companyOutstanding)}</td>
                    <td style={cell(false)}>{previousAdvanceCell(advance)}</td>
                    <td style={cell(false)}>
                      <Stack
                        primary={`Exp. ${monthYear(advance.expectedStartMonth)}`}
                        secondary={`Actual ${monthYear(advance.actualStartMonth)}`}
                      />
                    </td>
                    <td style={cell(false)}>
                      <Stack primary={monthsOrNA(advance.targetMonths)} secondary={monthYear(advance.targetDate)} />
                    </td>
                    <td style={cell(false)}>
                      {months === null || months === undefined ? (
                        <Stack primary="N/A" secondary="No projected recoup" />
                      ) : (
                        <Stack
                          primary={`${months} mo ${advance.isActual ? "actual" : "projected"}`}
                          secondary={monthYear(advance.companyRecoupMonth)}
                        />
                      )}
                    </td>
                    <td style={cell(true)}>
                      <Delta value={advance.recoupTimingDelta} />
                    </td>
                    <td style={cell(false)}>
                      <StatusPill status={advance.performanceStatus} />
                    </td>
                  </tr>
                )
              })}
            </tbody>
            {/* Column totals across the advances above. */}
            <tfoot>
              {(() => {
                const footStyle = (numeric: boolean): React.CSSProperties => ({
                  ...tdStyle(numeric),
                  fontWeight: 500,
                  padding: 12,
                  backgroundColor: "#f9fafb",
                  borderTop: "2px solid #e5e7eb",
                  borderBottom: "none"
                })
                return (
                  <tr>
                    <td style={footStyle(false)}>
                      TOTAL <Muted text={`· ${advances.length} ${advances.length === 1 ? "advance" : "advances"}`} />
                    </td>
                    <td style={footStyle(false)}>
                      <Muted text="—" />
                    </td>
                    <td style={footStyle(true)}>{usd0(totals.amount)}</td>
                    <td style={footStyle(true)}>{usd0(totals.outstanding)}</td>
                    <td style={footStyle(false)}>
                      <Muted text="—" />
                    </td>
                    <td style={footStyle(false)}>
                      <Muted text="—" />
                    </td>
                    <td style={footStyle(false)}>{monthsOrNA(totals.targetMonths)}</td>
                    <td style={footStyle(false)}>{monthsOrNA(totals.projectedMonths)}</td>
                    <td style={footStyle(true)}>
                      <Delta value={totalDelta} />
                    </td>
                    <td style={footStyle(false)}>
                      {totalStatus ? <StatusPill status={totalStatus} /> : <Muted text="N/A" />}
                    </td>
                  </tr>
                )
              })()}
            </tfoot>
          </table>
        </div>
      )}
    </div>
  )
}

export default AdvanceRecoupmentSchedule
